import moment from "moment";
import momentTz from "moment-timezone";
import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import {
  createCustomFixture,
  updateCategoryLocally,
} from "../../redux/slices/tournamentSlice";
import ClubbieModal from "../Modal/ClubbieModal";
import ClubbieSelect from "../ClubbieSelect/ClubbieSelect";
import DatePicker from "../DatePicker/DatePicker";
import TimePicker from "../TimePicker/TimePicker";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { fixturesGenerated, fixtureCreated } from "../../utils/analytics";
import { GENERATION_MODE } from "../../utils/analyticsEvents";
import { buildSlotOptions, NO_LINK_VALUE } from "../../pages/fixtures/utils/buildSlotOptions";

const INITIAL_FIXTURE_STATE = {
  homeTeam: null,
  awayTeam: null,
  matchDate: "",
  matchStartTime: "",
  matchEndTime: "",
  pitch: "",
  referees: [],
};

// Pitch on a fixture is stored as `"Pitch <n>"` (matching auto-gen output).
// The UI captures just the number; format/parse helpers bridge the two.
const formatPitchValue = (n) => {
  const num = parseInt(n, 10);
  if (!Number.isFinite(num) || num < 1) return undefined;
  return `Pitch ${num}`;
};

const FieldLabel = ({ children }) => (
  <p className="text-[15px] font-medium text-[#FEFCEB] m-0">{children}</p>
);

const CreateFixtureModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [fixture, setFixture] = React.useState(INITIAL_FIXTURE_STATE);
  const [selectedPhase, setSelectedPhase] = useState(null);
  // `selectedSlot` is the chosen "link target" — either the noLink sentinel
  // or a group / bracket-tie / non-custom single-match option.
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { selectedCategory, selectedTournament } = useSelector(
    (state) => state.tournament
  );

const phases = selectedCategory?.phases || [];
  const phaseOptions = useMemo(
    () => phases.map((p) => ({ label: p.name, value: p._id || p.id, _phase: p })),
    [phases]
  );

  // Enumerate every linkable slot in the chosen phase (no-link sentinel +
  // groups + bracket ties per leg + non-custom single-match blocks).
  const slotOptions = useMemo(
    () => buildSlotOptions(selectedPhase),
    [selectedPhase]
  );

  const teams = useMemo(() => {
    // Team picker scope follows the chosen slot:
    //   - group → restrict to that group's non-dummy members
    //   - bracket / singleMatch → any team in the category (broader pool)
    //   - noLink / no selection → fall back to phase teams, then category teams
    //
    // Group resolves each block.teams[].actualTeamId via the category's team
    // pool because some backends don't populate block.teams.actualTeamId,
    // leaving it as a raw ObjectId the select can't render.
    const categoryTeamById = new Map(
      (selectedCategory?.teams || []).map((t) => {
        const id = (t.teamId?._id || t.teamId?.id || t.teamId)?.toString?.();
        return [id, t.teamId];
      })
    );

    if (selectedSlot?.kind === "group" && selectedSlot.block) {
      return (selectedSlot.block.teams || [])
        .filter((t) => !t.isDummy && t.actualTeamId)
        .map((t) => {
          const id = (t.actualTeamId?._id || t.actualTeamId)?.toString?.();
          return categoryTeamById.get(id) || t.actualTeamId;
        })
        .filter((team) => team && (team.name || team.title));
    }
    if (selectedSlot?.kind === "bracket" || selectedSlot?.kind === "singleMatch") {
      return selectedCategory?.teams?.map((team) => team.teamId) || [];
    }
    const phaseTeams = selectedPhase?._phase?.teams;
    if (phaseTeams) return phaseTeams.map((t) => t.teamId || t);
    return selectedCategory?.teams?.map((team) => team.teamId) || [];
  }, [selectedSlot, selectedPhase, selectedCategory?.teams]);

  const awayTeamOptions = useMemo(
    () =>
      teams.filter((team) => {
        const homeTeamId = fixture?.homeTeam?._id || fixture?.homeTeam?.id;
        const teamId = team?._id || team?.id;
        return teamId !== homeTeamId;
      }),
    [teams, fixture?.homeTeam]
  );

  const refereeOptions = useMemo(() => {
    const refs = selectedTournament?.members?.filter((m) => m.role === "referee") || [];
    return refs
      .filter((r) => r.userId?.profile)
      .map((r) => ({
        label: r.userId.profile.fullName,
        value: r.userId.id,
      }));
  }, [selectedTournament]);

  const handleTeamSelect = useCallback((team, field) => {
    setFixture((prev) => {
      const updates = { [field]: team };
      if (field === "homeTeam" && team?.id === prev.awayTeam?.id) updates.awayTeam = null;
      if (field === "awayTeam" && team?.id === prev.homeTeam?.id) updates.homeTeam = null;
      return { ...prev, ...updates };
    });
  }, []);

  const handleDateChange = useCallback((date) => {
    setFixture((prev) => ({ ...prev, matchDate: date }));
  }, []);

  const handleTimeChange = useCallback((time, field) => {
    setFixture((prev) => ({ ...prev, [field]: time }));
  }, []);

  const handlePitchChange = useCallback((value) => {
    const digits = value.replace(/\D/g, "");
    setFixture((prev) => ({ ...prev, pitch: digits }));
  }, []);

  const handleRefereeSelect = useCallback((option) => {
    setFixture((prev) => ({ ...prev, referees: option ? [option] : [] }));
  }, []);

  const getAvailableDates = useCallback(() => {
    // Custom fixtures aren't bound to a phase's schedule — admins want the
    // full tournament window so they can slot a one-off match anywhere.
    const matchSchedule = selectedTournament?.matchSchedule;
    if (!matchSchedule?.startDate) return [];
    const startDate = moment(matchSchedule.startDate);
    const endDate = matchSchedule.endDate
      ? moment(matchSchedule.endDate)
      : startDate.clone().add(6, "months");
    const allowedDays = matchSchedule.matchDays || [];
    const allDates = [];
    let currentDate = startDate.clone();
    const maxDate = endDate.clone().add(1, "year");
    while (currentDate.isSameOrBefore(endDate) && currentDate.isBefore(maxDate)) {
      const dayName = currentDate.format("dddd");
      if (allowedDays.length === 0 || allowedDays.includes(dayName)) {
        allDates.push(currentDate.format("YYYY-MM-DD"));
      }
      currentDate.add(1, "day");
    }
    return allDates;
  }, [selectedTournament]);

  const handleCreate = useCallback(async () => {
    if (!selectedTournament || !selectedCategory) {
      toast.error("Tournament data is not fully loaded. Please wait.");
      return;
    }

    if (!selectedPhase) {
      toast.error("Please select a phase.");
      return;
    }

    const { homeTeam, awayTeam, matchDate, matchStartTime, pitch } = fixture;

    if (!homeTeam || !awayTeam) {
      toast.error("Please select both teams.");
      return;
    }

    try {
      setIsLoading(true);
      const loadingToast = toast.loading("Creating fixture...");

      const homeId = homeTeam._id || homeTeam.id;
      const awayId = awayTeam._id || awayTeam.id;

      // Convert the user's local date+time pair into a true UTC instant
      // using the browser's IANA timezone, then send timezone alongside so
      // the backend can persist it on the tournament.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const matchTimeUtc = momentTz
        .tz(`${matchDate} ${matchStartTime}`, "YYYY-MM-DD HH:mm", timezone)
        .toISOString();

      const payload = {
        categoryId: selectedCategory._id || selectedCategory.id,
        phaseId: selectedPhase.value,
        homeTeamId: homeId,
        awayTeamId: awayId,
        pitch: formatPitchValue(pitch),
        matchTime: matchTimeUtc,
        timezone,
        // Slot-link encoding: only set the API fields when the user picked a
        // real slot. The "No link" sentinel and "no selection" both omit
        // them, landing the fixture in the Custom Matches block.
        ...(selectedSlot && selectedSlot.kind !== "noLink"
          ? { targetBlockId: selectedSlot.blockId }
          : {}),
        ...(selectedSlot && selectedSlot.kind === "bracket"
          ? { targetFixtureId: selectedSlot.fixtureId }
          : {}),
      };

      const tournamentIdForEvent = selectedTournament?.id || selectedTournament?._id;
      const res = await dispatch(
        createCustomFixture({
          tournamentId: tournamentIdForEvent,
          fixtureData: payload,
        })
      ).unwrap();

      fixturesGenerated({
        generationMode: GENERATION_MODE.MANUAL,
        competitionId: tournamentIdForEvent,
        categoryId: payload.categoryId,
        fixtureCount: 1,
      });
      fixtureCreated({
        fixtureId: res?.fixture?._id || res?.fixture?.id || res?._id || res?.id,
        tournamentId: tournamentIdForEvent,
        categoryId: payload.categoryId,
        isCustom: true,
      });

      toast.dismiss(loadingToast);
      toast.success("Fixture created successfully!");

      try {
        const created = res?.fixture || res?.createdFixture || res?.match || res;
        const createdId = created?._id || created?.id || `tmp-${Date.now()}`;
        const optimisticFixture = {
          _id: createdId,
          matchTime: matchTimeUtc,
          pitch: formatPitchValue(pitch),
          homeTeamId: homeId,
          awayTeamId: awayId,
          eventId: created?.eventId || null,
        };
        const currentFixtures = Array.isArray(selectedCategory?.fixtures) ? selectedCategory.fixtures : [];
        const updatedCategory = {
          ...selectedCategory,
          fixtures: currentFixtures.some((f) => (f._id || f.id) === createdId)
            ? currentFixtures
            : [...currentFixtures, optimisticFixture],
        };
        dispatch(
          updateCategoryLocally({
            tournamentId: selectedTournament?.id || selectedTournament?._id,
            categoryId: selectedCategory?._id || selectedCategory?.id,
            updatedCategory,
          })
        );
      } catch (e) {
        // no-op
      }

      setFixture(INITIAL_FIXTURE_STATE);
      setSelectedPhase(null);
      setSelectedSlot(null);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.dismiss();

      let errorMessage = "Failed to create fixture";
      if (error?.message) {
        const msg = error.message;
        if (msg.includes("has no players")) {
          const teamMatch = msg.match(/team:([a-f0-9]+)/);
          const teamId = teamMatch ? teamMatch[1] : null;
          const hId = fixture.homeTeam?._id || fixture.homeTeam?.id;
          const aId = fixture.awayTeam?._id || fixture.awayTeam?.id;
          if (teamId === hId) {
            errorMessage = `${fixture.homeTeam?.name || fixture.homeTeam?.title || "Team 1"} has no approved players.`;
          } else if (teamId === aId) {
            errorMessage = `${fixture.awayTeam?.name || fixture.awayTeam?.title || "Team 2"} has no approved players.`;
          } else {
            errorMessage = "One of the teams has no approved players.";
          }
        } else {
          errorMessage = msg;
        }
      }

      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  }, [fixture, selectedTournament, selectedCategory, dispatch, onClose, onSuccess]);

  const isFormValid = useMemo(() => {
    const { homeTeam, awayTeam, matchDate, matchStartTime, matchEndTime, pitch } = fixture;
    const pitchNum = parseInt(pitch, 10);
    const pitchValid = Number.isFinite(pitchNum) && pitchNum >= 1;
    return selectedPhase && homeTeam && awayTeam && matchDate && matchStartTime && matchEndTime && pitchValid;
  }, [fixture, selectedPhase]);

  const getOptionLabel = useCallback((option) => option?.name || option?.title || "", []);
  const getOptionValue = useCallback((option) => option?._id || option?.id || "", []);
  const getOptionImage = useCallback((option) => option?.crest || null, []);

  const footer = (
    <div className="flex justify-end gap-3 w-full">
      <button
        onClick={onClose}
        className="px-6 py-3 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        onClick={handleCreate}
        disabled={!isFormValid || isLoading}
        className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-[15px] transition-opacity disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading ? <><LoadingSpinner size="small" /><span>Creating...</span></> : "Create fixture"}
      </button>
    </div>
  );

  if (!selectedTournament || !selectedCategory) {
    return (
      <ClubbieModal isOpen={isOpen} toggle={onClose} title="Create fixture" width="560px">
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <LoadingSpinner size="large" />
          <p className="text-app-text-secondary text-[15px]">Loading tournament data...</p>
        </div>
      </ClubbieModal>
    );
  }

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={onClose}
      title="Create fixture"
      width="560px"
      footer={footer}
    >
      <div className="space-y-5 py-1">
        {/* Phase */}
        <div className="space-y-2">
          <FieldLabel>Phase</FieldLabel>
          <ClubbieSelect
            placeholder="Select phase"
            options={phaseOptions}
            value={selectedPhase}
            onChange={(opt) => {
              setSelectedPhase(opt);
              // Slot options are scoped to the chosen phase, so any previous
              // pick is meaningless in the new phase. Reset both.
              setSelectedSlot(null);
              setFixture((prev) => ({ ...prev, homeTeam: null, awayTeam: null }));
            }}
            getOptionLabel={(opt) => opt?.label || ""}
            getOptionValue={(opt) => opt?.value || ""}
          />
        </div>

        {/* Link to existing match — only renders when the chosen phase has at
            least one linkable slot beyond the "No link" sentinel (group / bracket
            tie / non-custom single match). Picking "No link" lands the fixture
            in the Custom Matches block as a standalone match. */}
        {slotOptions.length > 1 && (
          <div className="space-y-2">
            <FieldLabel>Link to existing match (optional)</FieldLabel>
            <ClubbieSelect
              placeholder="Standalone custom match"
              options={slotOptions}
              value={selectedSlot}
              onChange={(opt) => {
                const next = opt && opt.value !== NO_LINK_VALUE ? opt : null;
                setSelectedSlot(next);
                // Team picker pool changes when the slot changes; reset
                // selections to avoid stale picks from the previous pool.
                setFixture((prev) => ({ ...prev, homeTeam: null, awayTeam: null }));
              }}
              getOptionLabel={(opt) => opt?.label || ""}
              getOptionValue={(opt) => opt?.value || ""}
            />
          </div>
        )}

        {/* Teams */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <FieldLabel>Home Team</FieldLabel>
            <ClubbieSelect
              placeholder="Select team"
              options={teams}
              value={fixture.homeTeam}
              onChange={(team) => handleTeamSelect(team, "homeTeam")}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              getOptionImage={getOptionImage}
              isSearchable
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Away Team</FieldLabel>
            <ClubbieSelect
              placeholder="Select team"
              options={awayTeamOptions}
              value={fixture.awayTeam}
              onChange={(team) => handleTeamSelect(team, "awayTeam")}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              getOptionImage={getOptionImage}
              isSearchable
            />
          </div>
        </div>

        {/* Match Date */}
        <div className="space-y-2">
          <FieldLabel>Match Date</FieldLabel>
          <DatePicker
            value={fixture.matchDate}
            onChange={handleDateChange}
            minDate={null}
            maxDate={null}
            availableDates={getAvailableDates()}
            disablePastDates={false}
          />
        </div>

        {/* Match Times */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <FieldLabel>Start Time</FieldLabel>
            <TimePicker
              value={fixture.matchStartTime}
              onChange={(time) => handleTimeChange(time, "matchStartTime")}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>End Time</FieldLabel>
            <TimePicker
              value={fixture.matchEndTime}
              onChange={(time) => handleTimeChange(time, "matchEndTime")}
            />
          </div>
        </div>

        {/* Pitch */}
        <div className="space-y-2">
          <FieldLabel>Pitch</FieldLabel>
          <input
            type="number"
            min="1"
            step="1"
            value={fixture.pitch}
            onChange={(e) => handlePitchChange(e.target.value)}
            placeholder="Pitch number (e.g. 1)"
            className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
          />
        </div>

        {/* Referees */}
        <div className="space-y-2">
          <FieldLabel>Referee</FieldLabel>
          <ClubbieSelect
            placeholder="Select referee"
            options={refereeOptions}
            value={fixture.referees?.[0] || null}
            onChange={handleRefereeSelect}
            getOptionLabel={(opt) => opt?.label || ""}
            getOptionValue={(opt) => opt?.value || ""}
          />
        </div>
      </div>
    </ClubbieModal>
  );
};

export default CreateFixtureModal;
